import { useTranslation } from 'react-i18next'

import { canDeleteCampaign, canStopCampaign } from '#/entities/campaign'
import type { CampaignRow } from '#/entities/campaign'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/shared/ui/dropdown-menu'
import { MoreHorizontalIcon } from '#/shared/ui/icons'

import { campaignLifecycleAction } from '../model/use-campaign-actions'
import type { CampaignRowActions } from '../model/use-campaign-row-actions'

/**
 * The one lifecycle item, worded for where the campaign actually is —
 * `running → Pause`, `paused → Resume`, `draft → Start`. A scheduled campaign
 * is already on its way and a finished or stopped one is over, so neither
 * offers anything to press.
 */
function LifecycleItem({
  row,
  actions,
}: {
  row: CampaignRow
  actions: CampaignRowActions
}) {
  const { t } = useTranslation()
  const action = campaignLifecycleAction(row.status)
  if (action === null) return null
  return (
    <DropdownMenuItem onSelect={() => actions.onLifecycle(row, action)}>
      {t(`campaigns.rowMenu.${action}`)}
    </DropdownMenuItem>
  )
}

/**
 * Delete, and the reason it is not available when it is not.
 *
 * A running or paused campaign has to be stopped first — the service says so
 * and would refuse the call — so the item stays visible but inert with the
 * reason beneath it. Hiding it would leave an agent hunting for a delete that
 * the row simply does not show today.
 */
function DeleteItem({
  row,
  actions,
}: {
  row: CampaignRow
  actions: CampaignRowActions
}) {
  const { t } = useTranslation()
  const allowed = canDeleteCampaign(row.status)
  return (
    <DropdownMenuItem
      variant="destructive"
      disabled={!allowed}
      onSelect={() => actions.onDeleteRequest(row)}
    >
      <span className="flex flex-col gap-0.5">
        <span>{t('campaigns.rowMenu.delete')}</span>
        {!allowed && (
          <span className="text-2xs text-gray-500">
            {t('campaigns.rowMenu.deleteBlocked')}
          </span>
        )}
      </span>
    </DropdownMenuItem>
  )
}

export function CampaignRowMenu({
  row,
  actions,
}: {
  row: CampaignRow
  actions: CampaignRowActions
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('campaigns.rowActions', { name: row.name })}
        disabled={actions.isBusy}
        className="flex size-6 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MoreHorizontalIcon className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => actions.onEdit(row)}>
          {t('campaigns.rowMenu.edit')}
        </DropdownMenuItem>
        <LifecycleItem row={row} actions={actions} />
        <DropdownMenuItem onSelect={() => actions.onDuplicate(row)}>
          {t('campaigns.rowMenu.duplicate')}
        </DropdownMenuItem>
        {canStopCampaign(row.status) && (
          <DropdownMenuItem onSelect={() => actions.onStop(row)}>
            {t('campaigns.rowMenu.stop')}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DeleteItem row={row} actions={actions} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
